import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  SETTINGS_MODAL,
  toHexColor,
} from '../config/gameConfig';
import type { EventBus } from '../systems/EventBus';
import type { ProceduralRates } from '../systems/ProceduralEvents';
import { propagateDepth } from './propagateDepth';

type RateKind = 'email' | 'payment' | 'call' | 'aging-tick';

interface RatePreset {
  label: string;
  value: number;
}

const PRESETS: readonly RatePreset[] = [
  { label: 'Off', value: 0 },
  { label: 'Slow', value: 0.5 },
  { label: 'Normal', value: 1 },
  { label: 'Fast', value: 1.5 },
];

const RATE_ROWS: readonly { kind: RateKind; label: string }[] = [
  { kind: 'email', label: 'Emails' },
  { kind: 'payment', label: 'Payments (wires)' },
  { kind: 'aging-tick', label: 'Aging-ticks' },
  { kind: 'call', label: 'Calls / voicemails' },
];

const STORAGE_KEY = 'ar-rush:settings:rates';

const loadRates = (): ProceduralRates => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { email: 1, payment: 1, call: 1, 'aging-tick': 1, paused: false };
    const parsed = JSON.parse(raw) as Partial<ProceduralRates>;
    return {
      email: typeof parsed.email === 'number' ? parsed.email : 1,
      payment: typeof parsed.payment === 'number' ? parsed.payment : 1,
      call: typeof parsed.call === 'number' ? parsed.call : 1,
      'aging-tick': typeof parsed['aging-tick'] === 'number' ? parsed['aging-tick'] : 1,
      paused: parsed.paused === true,
    };
  } catch {
    return { email: 1, payment: 1, call: 1, 'aging-tick': 1, paused: false };
  }
};

const saveRates = (rates: ProceduralRates): void => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(rates)); } catch { /* ignore */ }
};

export class SettingsModal {
  private container: Phaser.GameObjects.Container | null = null;
  private rates: ProceduralRates;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;

  private readonly onOpenRequested = (): void => {
    if (this.container) return;
    this.open();
  };

  constructor(private readonly scene: Phaser.Scene, private readonly bus: EventBus) {
    this.rates = loadRates();
    // Push the persisted rates immediately so ProceduralEvents picks them up
    // even before the modal is opened the first time.
    this.bus.emit('settings:rates-changed', { rates: { ...this.rates } });
    bus.on('settings:open-requested', this.onOpenRequested);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  /** Initial rates loaded from localStorage. ProceduralEvents reads these on
   *  construction so the persisted preference is honored from the first tick. */
  initialRates(): ProceduralRates {
    return { ...this.rates };
  }

  private open(): void {
    const x = Math.round((GAME_WIDTH - SETTINGS_MODAL.width) / 2);
    const y = Math.round((GAME_HEIGHT - SETTINGS_MODAL.height) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, SETTINGS_MODAL.backdropAlpha)
      .setOrigin(0, 0);
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, SETTINGS_MODAL.width, SETTINGS_MODAL.height, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const title = this.scene.add.text(
      x + SETTINGS_MODAL.paddingX,
      y + SETTINGS_MODAL.paddingY,
      'Pace settings',
      {
        fontFamily: FONTS.ui,
        fontSize: SETTINGS_MODAL.titleFontSize,
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    const subtitle = this.scene.add.text(
      x + SETTINGS_MODAL.paddingX,
      y + SETTINGS_MODAL.paddingY + title.height + 4,
      'How fast each kind of work shows up. Saved across sessions.',
      {
        fontFamily: FONTS.ui,
        fontSize: '12px',
        color: toHexColor(COLORS.textMuted),
      },
    );
    root.add(subtitle);

    // Close (X) top-right
    const closeX = x + SETTINGS_MODAL.width - SETTINGS_MODAL.paddingX - SETTINGS_MODAL.closeButtonSize / 2;
    const closeY = y + SETTINGS_MODAL.paddingY + SETTINGS_MODAL.closeButtonSize / 2 - 4;
    const closeBtn = this.scene.add
      .circle(closeX, closeY, SETTINGS_MODAL.closeButtonSize / 2, COLORS.windowControlClose)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => this.close());
    root.add(closeBtn);
    const closeIcon = this.scene.add.image(closeX, closeY, 'icon:x')
      .setDisplaySize(12, 12)
      .setOrigin(0.5, 0.5);
    root.add(closeIcon);

    let rowY = y + SETTINGS_MODAL.paddingY + 56;
    for (const row of RATE_ROWS) {
      this.renderRateRow(root, x, rowY, row.kind, row.label);
      rowY += SETTINGS_MODAL.rowHeight + SETTINGS_MODAL.rowGap;
    }

    // Pause toggle at the bottom.
    rowY += 8;
    this.renderPauseToggle(root, x, rowY);

    this.escHandler = (ev: KeyboardEvent) => { if (ev.key === 'Escape') this.close(); };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);
  }

  private renderRateRow(
    root: Phaser.GameObjects.Container,
    modalX: number,
    rowY: number,
    kind: RateKind,
    label: string,
  ): void {
    const labelText = this.scene.add.text(
      modalX + SETTINGS_MODAL.paddingX,
      rowY + SETTINGS_MODAL.rowHeight / 2,
      label,
      {
        fontFamily: FONTS.ui,
        fontSize: SETTINGS_MODAL.rowFontSize,
        color: toHexColor(COLORS.textPrimary),
      },
    ).setOrigin(0, 0.5);
    root.add(labelText);

    const buttonsRightEdge = modalX + SETTINGS_MODAL.width - SETTINGS_MODAL.paddingX;
    const totalButtonsW = PRESETS.length * SETTINGS_MODAL.buttonWidth
      + (PRESETS.length - 1) * SETTINGS_MODAL.buttonGap;
    let cursorX = buttonsRightEdge - totalButtonsW;
    const buttonY = rowY + (SETTINGS_MODAL.rowHeight - SETTINGS_MODAL.buttonHeight) / 2;

    const buttons: Phaser.GameObjects.Rectangle[] = [];
    PRESETS.forEach((preset, i) => {
      const isActive = Math.abs(this.rates[kind] - preset.value) < 0.01;
      const fill = isActive ? COLORS.buttonPrimary : COLORS.windowChrome;
      const btn = this.scene.add
        .rectangle(cursorX, buttonY, SETTINGS_MODAL.buttonWidth, SETTINGS_MODAL.buttonHeight, fill)
        .setOrigin(0, 0)
        .setStrokeStyle(1, COLORS.windowBorder)
        .setInteractive({ useHandCursor: true });
      const labelEl = this.scene.add.text(
        cursorX + SETTINGS_MODAL.buttonWidth / 2,
        buttonY + SETTINGS_MODAL.buttonHeight / 2,
        preset.label,
        {
          fontFamily: FONTS.ui,
          fontSize: SETTINGS_MODAL.buttonFontSize,
          color: toHexColor(isActive ? COLORS.buttonPrimaryText : COLORS.textPrimary),
          fontStyle: isActive ? 'bold' : 'normal',
        },
      ).setOrigin(0.5, 0.5);
      btn.on('pointerdown', () => {
        this.rates = { ...this.rates, [kind]: preset.value };
        saveRates(this.rates);
        this.bus.emit('settings:rates-changed', { rates: { ...this.rates } });
        // Refresh visual state on this row.
        buttons.forEach((b, j) => {
          const a = j === i;
          b.setFillStyle(a ? COLORS.buttonPrimary : COLORS.windowChrome);
        });
      });
      root.add(btn);
      root.add(labelEl);
      buttons.push(btn);
      cursorX += SETTINGS_MODAL.buttonWidth + SETTINGS_MODAL.buttonGap;
    });
  }

  private renderPauseToggle(root: Phaser.GameObjects.Container, modalX: number, rowY: number): void {
    const w = SETTINGS_MODAL.width - SETTINGS_MODAL.paddingX * 2;
    const fill = this.rates.paused ? COLORS.warning : COLORS.windowChrome;
    const btn = this.scene.add
      .rectangle(modalX + SETTINGS_MODAL.paddingX, rowY, w, SETTINGS_MODAL.pauseButtonHeight, fill)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    const label = this.scene.add.text(
      modalX + SETTINGS_MODAL.width / 2,
      rowY + SETTINGS_MODAL.pauseButtonHeight / 2,
      this.rates.paused ? 'Paused — click to resume' : 'Pause all incoming events',
      {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5);
    btn.on('pointerdown', () => {
      this.rates = { ...this.rates, paused: !this.rates.paused };
      saveRates(this.rates);
      this.bus.emit('settings:rates-changed', { rates: { ...this.rates } });
      btn.setFillStyle(this.rates.paused ? COLORS.warning : COLORS.windowChrome);
      label.setText(this.rates.paused ? 'Paused — click to resume' : 'Pause all incoming events');
    });
    root.add(btn);
    root.add(label);
  }

  private close(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }

  private teardown(): void {
    this.bus.off('settings:open-requested', this.onOpenRequested);
    this.close();
  }
}
